import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { colors, black, book, regular, semiBold, getDropShadow } from '@/styles';
/**
 * Trial Banner
 *
 * A banner to display the number of days left in a users account
 *
 * @param {import('./TrialBanner').TrialBannerProps} props
 */
function TrialBanner({
  className,
  compact,
  daysLeft,
  displayBuyNow,
  enableExpiredMessage,
  onBuyNowClick,
  isRebrand,
}) {
  // hide if days left hasn't been supplied
  if (daysLeft === null || daysLeft === undefined) {
    return null;
  }

  const dayOrDays = ` day${daysLeft === 1 ? '' : 's'}`;

  return (
    <div
      className={classNames(className, { 'past-expiration': enableExpiredMessage && daysLeft < 0 })}
    >
      <div className="days-left-container">
        {daysLeft === 0 && (
          <>
            <span className="days-left">Last Day </span>
            {!compact && <span>in your BombBomb trial </span>}
            {compact && <span>of your trial</span>}
          </>
        )}
        {daysLeft > 0 && !compact && (
          <>
            <span className="days-left">Your free trial </span>
            <span>is expiring in </span>
            <span className="days-left">{daysLeft}</span>
            <span>{dayOrDays}</span>
          </>
        )}
        {daysLeft > 0 && compact && (
          <>
            <span className="days-left">
              {/* prettier and eslint need to figure their life out */}
              {}
              {daysLeft} {dayOrDays.toUpperCase()} LEFT{' '}
            </span>
            <span>in your trial</span>
          </>
        )}
        {enableExpiredMessage && daysLeft < 0 && <>Your trial has expired.</>}
      </div>
      {displayBuyNow && (
        <button type="button" className="buy-now-button" onClick={onBuyNowClick}>
          BUY NOW
        </button>
      )}
    </div>
  );
}

TrialBanner.propTypes = {
  /** CSS Class Name(s). */
  className: PropTypes.string.isRequired,
  /** Should the display be normal or compact */
  compact: PropTypes.bool,
  /** How many days does the user have left */
  daysLeft: PropTypes.number,
  /** Displays the buy now button. */
  displayBuyNow: PropTypes.bool,
  /** Determines if the banner will turn orange and display "Your trial has expired". */
  enableExpiredMessage: PropTypes.bool,
  /** A handler for when the "Buy Now" button is clicked  */
  onBuyNowClick: PropTypes.func,
  isRebrand: PropTypes.bool,
};

TrialBanner.defaultProps = {
  compact: false,
  daysLeft: null,
  displayBuyNow: true,
  enableExpiredMessage: false,
  onBuyNowClick: () => {},
  isRebrand: false,
};

const styledTrialBanner = styled(TrialBanner)`
  ${({ isRebrand }) => (isRebrand ? regular() : book())}
  background-color: ${colors.taosSky};
  color: ${colors.white};
  padding: ${({ compact }) => (compact ? `${14 / 16}rem ${6 / 16}rem` : '1rem')};
  text-align: ${({ compact }) => (compact ? 'left' : 'center')};
  ${({ compact }) => {
    if (compact) {
      return `
        display: flex;
        align-items: center;
        justify-content: center;
      `;
    }
    return '';
  }}

  .days-left-container {
    display: inline-block;
    margin: ${({ compact }) => (compact ? '0 0.5rem 0 0' : '0.1rem 0.8rem')};

    .days-left {
      ${({ isRebrand }) => (isRebrand ? semiBold() : black())}
      font-weight: 700;
    }
  }

  .buy-now-button {
    background-color: ${colors.taosSkyMed2};
    border: 0;
    color: ${colors.white};
    display: inline-block;
    ${({ isRebrand }) => (isRebrand ? semiBold() : black())}
    margin: ${({ compact }) => (compact ? '' : '0.1rem 0.8rem')};
    padding: 0.2rem 0.8rem;
    transition: background-color 80ms ease-in;

    &:hover,
    &:focus {
      cursor: pointer;
      background-color: ${colors.taosSkyDark};
    }

    &:focus {
      outline: 0;
      filter: ${getDropShadow(colors.taosSkyLight)};
    }

    &:active {
      background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)), ${colors.taosSkyDark};
    }
  }

  &.past-expiration {
    background-color: ${colors.trafficCone};

    .buy-now-button {
      background-color: ${colors.trafficConeMed2};

      &:hover,
      &:focus {
        background-color: ${colors.trafficConeDark};
      }

      &:focus {
        filter: ${getDropShadow(colors.trafficConeLight)};
      }

      &:active {
        background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15)),
          ${colors.trafficConeDark};
      }
    }
  }
`;

export default styledTrialBanner;
